import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Trans } from 'react-i18next'
import { useSelector } from 'react-redux'

import lottie from 'lottie-web/build/player/lottie_light'
import confettiAnimation from '~/assets/animations/confetti.json'
import SectionProgress from '~/pages/anatomyV2/libs/components/SectionProgress'
import { TOTAL_SECTIONS } from '~/pages/anatomyV2/libs/components/SectionProgress/constants'
import { ScratchCard } from '~/shared/components/ScratchCard'
import { Cohort } from '~/shared/constants/commonConstants'
import { useNextStep } from '~/shared/hooks/useNextStep'
import { eventLogger } from '~/shared/services/eventLogger'
import {
  selectCohortID,
  selectDynamicDiscount,
} from '~/shared/store/common/selects'
import { selectUserEmail } from '~/shared/store/user/selects'
import { TPageProps } from '~/shared/types/commonTypes'

import { Styles as S } from './styles'

const DEFAULT_DISCOUNT = 56

const ScratchAnatomy = ({ nextPagePath, pageId }: TPageProps) => {
  const cohort = useSelector(selectCohortID)
  const isAnatomyV2 = cohort === Cohort.ANATOMY_V2
  const isGeneral = cohort === Cohort.GENERAL
  const discount = useSelector(selectDynamicDiscount)
  const email = useSelector(selectUserEmail)
  const [isScratchingFinished, setIsScratchingFinished] = useState(false)
  const [isConfettiVisible, setIsConfettiVisible] = useState(false)
  const confettiRef = useRef<HTMLDivElement>(null)
  const confettiTimerRef = useRef<ReturnType<typeof setTimeout>>()
  const scratchStartedRef = useRef(false)
  const scratchAttemptsRef = useRef(0)

  const goToNextPage = useNextStep({
    nextPagePath,
    pageId,
    pageName: pageId,
    question: 'scratch_card',
  })

  useEffect(() => {
    if (confettiRef.current && isConfettiVisible) {
      lottie.loadAnimation({
        container: confettiRef.current,
        animationData: confettiAnimation,
        name: 'confetti',
        autoplay: true,
        loop: false,
        rendererSettings: {
          preserveAspectRatio: 'xMidYMin slice',
        },
      })
    }

    return () => lottie.destroy('confetti')
  }, [isConfettiVisible])

  useEffect(() => {
    if (isScratchingFinished) {
      document.body.setAttribute('data-header-dark', 'true')
    }
    return () => {
      document.body.removeAttribute('data-header-dark')
    }
  }, [isScratchingFinished])

  useEffect(() => {
    eventLogger.logScratchDiscountPageShow()
  }, [])

  useEffect(() => {
    return () => clearTimeout(confettiTimerRef.current)
  }, [])

  const handleStart = useCallback(() => {
    scratchAttemptsRef.current += 1
    if (!scratchStartedRef.current) {
      scratchStartedRef.current = true
      eventLogger.logScratchingStarted()
    }
  }, [])

  const handleComplete = useCallback(() => {
    setIsScratchingFinished(true)
    eventLogger.logScratchingFinished(scratchAttemptsRef.current)
    confettiTimerRef.current = setTimeout(() => setIsConfettiVisible(true), 500)
  }, [])

  const handleContinue = () => goToNextPage('')

  return (
    <S.Wrapper $isGeneral={isGeneral}>
      <S.DarkOverlay $visible={isScratchingFinished} />

      <S.StyledContainer>
        {isAnatomyV2 && (
          <S.ProgressWrapper>
            <SectionProgress
              currentSection={TOTAL_SECTIONS + 1}
              pageId={pageId}
            />
          </S.ProgressWrapper>
        )}

        <S.Title>
          <Trans i18nKey="onboarding.scratchAnatomy.title" />
        </S.Title>

        <S.Subtitle>
          <Trans i18nKey="onboarding.scratchAnatomy.subtitle" />
        </S.Subtitle>

        <S.ScratchSection $finished={isScratchingFinished}>
          <ScratchCard
            discount={discount?.amount ?? DEFAULT_DISCOUNT}
            email={email}
            isDark={isScratchingFinished}
            onStart={handleStart}
            onComplete={handleComplete}
          />
          {isConfettiVisible && (
            <S.AppliedText>
              <Trans i18nKey="onboarding.scratchAnatomy.appliedAutomatically" />
            </S.AppliedText>
          )}
        </S.ScratchSection>

        <S.BottomSection>
          {isConfettiVisible && (
            <S.ContinueButton type="button" onClick={handleContinue}>
              <Trans i18nKey="actions.continue" />
            </S.ContinueButton>
          )}
        </S.BottomSection>
      </S.StyledContainer>

      {isConfettiVisible &&
        createPortal(<S.ConfettiAnimation ref={confettiRef} />, document.body)}
    </S.Wrapper>
  )
}

export default ScratchAnatomy
